import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { apiHandler } from '../../../../utils/api/responseHandler';
import config from '../../../../config';
import logger from '../../../../utils/logging/logger';
import { useComponentVersion } from '../../../../hooks/quoter/useComponentVersion';
import SegmentedControl from './SegmentedControl';

const componentName = 'LaminateToggle';

interface LaminateToggleProps {
    quoteId: number;
    sailId: number;
    sailCutId: number;
}

interface ClothDefaultResponse {
    OverrideClothId: number;
    Manufacturer: string;
    ClothMakeName: string;
    WeightOz: number;
    IsLaminate: number;
    PriceDelta: number;
    HasLaminateOption: number;
}

interface ClothTypeResponse {
    RecordsEffected: number;
    IsLaminate: number;
}

const TOGGLE_OPTIONS = [
    { value: 0, label: 'Dacron' },
    { value: 1, label: 'Laminate' }
];

const LaminateToggle: React.FC<LaminateToggleProps> = ({ quoteId, sailId, sailCutId }) => {
    const [isLaminate, setIsLaminate] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasLaminateOption, setHasLaminateOption] = useState(false);
    const updatingRef = useRef(false);

    // Only show for tri-radial (SailCutId=2) mainsails (SailId=1) and headsails (SailId=3)
    const showToggle = sailCutId === 2 && (sailId === 1 || sailId === 3);

    const fetchClothDefault = useCallback(async () => {
        if (!quoteId || !showToggle) return;

        try {
            const response = await apiHandler.fetch<ClothDefaultResponse>(
                `${config.API_URL}/api/quotes/${quoteId}/cloth-default`
            );

            if (response.error) throw response.error;

            if (response.data) {
                setIsLaminate(response.data.IsLaminate || 0);
                setHasLaminateOption(response.data.HasLaminateOption === 1);
                setIsInitialized(true);
            }
        } catch (err) {
            logger.log('LogError', componentName, 'Error fetching cloth default', err);
        }
    }, [quoteId, showToggle]);

    const { requestVersionUpdate } = useComponentVersion('clothDefault', {
        onVersionChange: fetchClothDefault
    });

    useEffect(() => {
        fetchClothDefault();
    }, [fetchClothDefault]);

    const handleToggle = useCallback(async (newValue: number) => {
        if (updatingRef.current || newValue === isLaminate) return;
        updatingRef.current = true;
        setIsLoading(true);

        const previousValue = isLaminate;

        try {
            const response = await apiHandler.fetch<ClothTypeResponse>(
                `${config.API_URL}/api/quotes/${quoteId}/cloth-type`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isLaminate: newValue })
                }
            );

            if (response.error) throw response.error;

            if (response.data && response.data.RecordsEffected === 0) {
                setIsLaminate(previousValue);
                logger.log('LogComponentUpdates', componentName,
                    'No laminate default configured for this boat/sail combination');
                return;
            }

            setIsLaminate(newValue);
            requestVersionUpdate();
        } catch (err) {
            logger.log('LogError', componentName, 'Error updating cloth type', err);
            setIsLaminate(previousValue);
        } finally {
            updatingRef.current = false;
            setIsLoading(false);
        }
    }, [quoteId, isLaminate, requestVersionUpdate]);

    // Hide if not tri-radial headsail/mainsail, not initialized, or no laminate option exists
    if (!showToggle || !isInitialized || !hasLaminateOption) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                width: '100%'
            }}
        >
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Cloth Type
            </Typography>
            <SegmentedControl
                options={TOGGLE_OPTIONS}
                value={isLaminate}
                onChange={handleToggle}
                disabled={isLoading}
            />
            {isLoading && <CircularProgress size={16} />}
        </Box>
    );
};

export default LaminateToggle;
